import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';

interface Producto {
    id: number;
    producto: string;
    descripcion: string;
    cantidad: number;
    idSucursalDestino?: number;
    cveDepartamentoDestino?: string;
    nombreUsuario?: string;
    observaciones?: string;
}

interface ResponsiveProductsTableProps {
    products: Producto[];
    catalogos: {
        sucursales: any[];
        departamentos: any[];
    };
    appState: {
        mode: 'view' | 'new' | 'edit';
    };
    onEditProduct: (product: Producto, index: number) => void;
    onDeleteProduct: (index: number) => void;
}

const ResponsiveProductsTable: React.FC<ResponsiveProductsTableProps> = ({
    products,
    catalogos,
    appState,
    onEditProduct,
    onDeleteProduct
}) => {
    const [isMobile, setIsMobile] = useState(false);

    // Detectar si es móvil
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1200);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Componentes de renderizado para tabla de escritorio
    const actionBodyTemplate = (rowData: Producto, options: any) => (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <Button
                icon="pi pi-pencil"
                size="small"
                severity="warning"
                rounded
                onClick={() => onEditProduct(rowData, options.rowIndex)}
                disabled={appState.mode === 'view'}
                tooltip="Editar producto"
            />
            <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                rounded
                onClick={() => onDeleteProduct(options.rowIndex)}
                disabled={appState.mode === 'view'}
                tooltip="Eliminar producto"
            />
        </div>
    );

    const cantidadBodyTemplate = (rowData: Producto) => (
        <div style={{ textAlign: 'center' }}>
            <Badge value={rowData.cantidad} severity="info" />
        </div>
    );

    const sucursalBodyTemplate = (rowData: Producto) => {
        const sucursal = catalogos.sucursales.find(s => s.id === rowData.idSucursalDestino);
        return (
            <span style={{
                padding: '4px 8px',
                backgroundColor: sucursal ? '#dbeafe' : '#fef2f2',
                color: sucursal ? '#1e40af' : '#dc2626',
                borderRadius: '4px',
                fontSize: '0.8rem'
            }}>
                {sucursal ? sucursal.nombre : 'No encontrada'}
            </span>
        );
    };

    const departamentoBodyTemplate = (rowData: Producto) => {
        const depto = catalogos.departamentos.find(d => d.cve === rowData.cveDepartamentoDestino);
        return (
            <span style={{
                padding: '4px 8px',
                backgroundColor: depto ? '#dcfce7' : '#fef2f2',
                color: depto ? '#166534' : '#dc2626',
                borderRadius: '4px',
                fontSize: '0.8rem'
            }}>
                {depto ? depto.nombre : 'No encontrado'}
            </span>
        );
    };

    const observacionesBodyTemplate = (rowData: Producto) => (
        <span style={{ 
            fontSize: '0.85rem',
            color: rowData.observaciones ? '#374151' : '#9ca3af',
            fontStyle: rowData.observaciones ? 'normal' : 'italic'
        }}>
            {rowData.observaciones || 'Sin observaciones'}
        </span>
    );

    // Renderizado de tarjetas móviles
    const renderMobileCards = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {products.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '40px 20px',
                    color: '#6b7280', fontSize: '1rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                }}>
                    📦 No hay productos agregados
                    <br />
                    <span style={{ fontSize: '0.85rem', marginTop: '8px', display: 'block' }}>
                        Utilice el formulario superior para agregar productos
                    </span>
                </div>
            ) : (
                products.map((product, index) => {
                    const sucursal = catalogos.sucursales.find(s => s.id === product.idSucursalDestino);
                    const depto = catalogos.departamentos.find(d => d.cve === product.cveDepartamentoDestino);
                    
                    return (
                        <div
                            key={product.id}
                            style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '16px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                position: 'relative'
                            }}
                        >
                            {/* Header de la tarjeta */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '12px',
                                paddingBottom: '8px',
                                borderBottom: '1px solid #f3f4f6'
                            }}>
                                <div>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        fontWeight: '500',
                                        marginBottom: '2px'
                                    }}>
                                        ID: {product.id}
                                    </div>
                                    <div style={{
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        color: '#1f2937'
                                    }}>
                                        🔑 {product.producto}
                                    </div>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <Badge 
                                        value={product.cantidad} 
                                        severity="info"
                                        style={{ fontSize: '0.8rem' }}
                                    />
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <Button
                                            icon="pi pi-pencil"
                                            size="small"
                                            severity="warning"
                                            rounded
                                            onClick={() => onEditProduct(product, index)}
                                            disabled={appState.mode === 'view'}
                                            tooltip="Editar"
                                            style={{ width: '28px', height: '28px' }}
                                        />
                                        <Button
                                            icon="pi pi-trash"
                                            size="small"
                                            severity="danger"
                                            rounded
                                            onClick={() => onDeleteProduct(index)}
                                            disabled={appState.mode === 'view'}
                                            tooltip="Eliminar"
                                            style={{ width: '28px', height: '28px' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contenido de la tarjeta */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                    <span style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        fontWeight: '500'
                                    }}>
                                        📝 Descripción:
                                    </span>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#374151',
                                        marginTop: '2px'
                                    }}>
                                        {product.descripcion}
                                    </div>
                                </div>

                                <div>
                                    <span style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        fontWeight: '500'
                                    }}>
                                        👤 Usuario:
                                    </span>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#374151',
                                        marginTop: '2px'
                                    }}>
                                        {product.nombreUsuario}
                                    </div>
                                </div>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '12px',
                                    marginTop: '8px'
                                }}>
                                    <div>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            fontWeight: '500'
                                        }}>
                                            🏢 Sucursal:
                                        </span>
                                        <div style={{ marginTop: '4px' }}>
                                            <span style={{
                                                padding: '3px 6px',
                                                backgroundColor: sucursal ? '#dbeafe' : '#fef2f2',
                                                color: sucursal ? '#1e40af' : '#dc2626',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                            }}>
                                                {sucursal ? sucursal.nombre : 'No encontrada'}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            fontWeight: '500'
                                        }}>
                                            🏬 Departamento:
                                        </span>
                                        <div style={{ marginTop: '4px' }}>
                                            <span style={{
                                                padding: '3px 6px',
                                                backgroundColor: depto ? '#dcfce7' : '#fef2f2',
                                                color: depto ? '#166534' : '#dc2626',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                            }}>
                                                {depto ? depto.nombre : 'No encontrado'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {product.observaciones && (
                                    <div style={{ marginTop: '8px' }}>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            fontWeight: '500'
                                        }}>
                                            💬 Observaciones:
                                        </span>
                                        <div style={{
                                            fontSize: '0.85rem',
                                            color: '#374151',
                                            marginTop: '2px',
                                            padding: '8px',
                                            backgroundColor: '#f9fafb',
                                            borderRadius: '6px',
                                            border: '1px solid #e5e7eb'
                                        }}>
                                            {product.observaciones}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );

    // Renderizado de tabla de escritorio
    const renderDesktopTable = () => (
        <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
        }}>
            <DataTable
                value={products}
                responsiveLayout="scroll"
                stripedRows
                size="small"
                emptyMessage={
                    <div style={{
                        textAlign: 'center', padding: '40px 20px',
                        color: '#6b7280', fontSize: '1rem'
                    }}>
                        📦 No hay productos agregados
                        <br />
                        <span style={{ fontSize: '0.85rem', marginTop: '8px', display: 'block' }}>
                            Utilice el formulario superior para agregar productos
                        </span>
                    </div>
                }
                className="p-datatable-sm"
                style={{ fontSize: '0.9rem' }}
                scrollable
                scrollHeight="400px"
            >
                <Column 
                    field="id" 
                    header="ID" 
                    headerStyle={{ 
                        width: '60px', textAlign: 'center', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ textAlign: 'center', padding: '8px' }}
                />
                <Column 
                    field="producto" 
                    header="🔑 Producto" 
                    headerStyle={{ 
                        width: '120px', textAlign: 'center', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ textAlign: 'center', padding: '8px', fontSize: '0.85rem' }}
                />
                <Column 
                    field="descripcion" 
                    header="📝 Descripción" 
                    headerStyle={{ 
                        minWidth: '200px', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ padding: '8px' }}
                />
                <Column 
                    field="cantidad" 
                    header="🔢 Cantidad" 
                    headerStyle={{ 
                        width: '100px', textAlign: 'center', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    body={cantidadBodyTemplate}
                    bodyStyle={{ textAlign: 'center', padding: '8px' }}
                />
                <Column 
                    field="nombreUsuario" 
                    header="👤 Usuario" 
                    headerStyle={{ 
                        minWidth: '150px', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ padding: '8px' }}
                />
                <Column 
                    field="idSucursalDestino" 
                    header="🏢 Sucursal" 
                    headerStyle={{ 
                        minWidth: '120px', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }}
                    body={sucursalBodyTemplate}
                    bodyStyle={{ padding: '8px' }}
                />
                <Column 
                    field="cveDepartamentoDestino" 
                    header="🏬 Departamento" 
                    headerStyle={{ 
                        minWidth: '120px', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }}
                    body={departamentoBodyTemplate}
                    bodyStyle={{ padding: '8px' }}
                />
                <Column 
                    field="observaciones" 
                    header="💬 Observaciones" 
                    headerStyle={{ 
                        minWidth: '150px', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ padding: '8px' }}
                    body={observacionesBodyTemplate}
                />
                <Column 
                    body={actionBodyTemplate} 
                    header="⚙️ Acciones" 
                    headerStyle={{ 
                        width: '120px', textAlign: 'center', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ textAlign: 'center', padding: '8px' }}
                />
            </DataTable>
        </div>
    );

    return (
        <>
            {/* CSS para responsive */}
            <style>{`
                @media screen and (max-width: 1200px) {
                    .p-datatable {
                        display: none !important;
                    }
                }
                
                @media screen and (min-width: 1200px) {
                    .mobile-cards {
                        display: none !important;
                    }
                }
                
                @media screen and (max-width: 1200px) {
                    .mobile-card {
                        margin: 0 -8px;
                        border-radius: 8px !important;
                    }
                    
                    .mobile-card-content {
                        padding: 12px !important;
                    }
                    
                    .mobile-card-header {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 8px !important;
                    }
                    
                    .mobile-card-actions {
                        align-self: flex-end !important;
                    }
                }
            `}</style>

            {/* Renderizado condicional */}
            <div className="desktop-table">
                {renderDesktopTable()}
            </div>
            
            <div className="mobile-cards">
                {renderMobileCards()}
            </div>

            {/* Resumen de productos */}
            {products.length > 0 && (
                <div style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px'
                }}>
                    <span style={{ 
                        color: '#0369a1', 
                        fontWeight: '600',
                        fontSize: isMobile ? '0.85rem' : '1rem'
                    }}>
                        📊 Total de productos: {products.length}
                    </span>
                    <span style={{ 
                        color: '#0369a1', 
                        fontWeight: '600',
                        fontSize: isMobile ? '0.85rem' : '1rem'
                    }}>
                        🔢 Cantidad total: {products.reduce((sum, p) => sum + p.cantidad, 0)}
                    </span>
                </div>
            )}
        </>
    );
};

export default ResponsiveProductsTable;